import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { CalendarDays, FileText, Target, Users, UserCheck, TrendingUp, Star, Loader2, BarChart3 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function KpiDashboard({ token }) {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/eventos/kpis/summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setKpis(data); })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!kpis) return <p className="text-center text-muted-foreground py-8">No hay datos de KPIs</p>;

  const metrics = [
    { label: "Total Eventos", value: kpis.total_events, icon: CalendarDays, color: "text-blue-600" },
    { label: "Con Seguimiento", value: kpis.events_with_followups, icon: FileText, color: "text-green-600" },
    { label: "Total Leads", value: kpis.total_leads, icon: Target, color: "text-amber-600" },
    { label: "Total Contactos", value: kpis.total_contacts, icon: Users, color: "text-purple-600" },
    { label: "Total Asistentes", value: kpis.total_attendees, icon: UserCheck, color: "text-teal-600" },
  ];

  return (
    <div className="space-y-6" data-testid="kpi-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {metrics.map(({ label, value, icon: Icon, color }) => (
          <Card key={label}>
            <CardContent className="pt-6 text-center">
              <Icon className={`w-6 h-6 mx-auto mb-2 ${color}`} />
              <p className="text-3xl font-bold">{value}</p>
              <p className="text-xs text-muted-foreground mt-1">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      {Object.keys(kpis.by_year).length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" />Desglose por Año</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(kpis.by_year).map(([y, d]) => (
                <div key={y} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="font-semibold text-lg">{y}</span>
                  <div className="flex gap-6 text-sm">
                    <span><strong>{d.leads}</strong> leads</span>
                    <span><strong>{d.contacts}</strong> contactos</span>
                    <span><strong>{d.attendees}</strong> asistentes</span>
                    <span><strong>{d.events}</strong> seguimientos</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      {kpis.top_events.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Star className="w-5 h-5 text-amber-500" />Top Eventos por Impacto</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {kpis.top_events.map((evt, i) => (
                <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">{evt.name}</p>
                    <p className="text-xs text-muted-foreground">{evt.years.join(", ")}</p>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <Badge variant="secondary">{evt.leads} leads</Badge>
                    <Badge variant="outline">{evt.contacts} contactos</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
